// @ts-ignore
import { getUserData } from "@/auth/getUserData";
import ChatsListChatInterface from "@/interfaces/inbox/chatsListChat.interface";
// @ts-ignore
import i18n from "@/libs/i18n";
import { useCustomersStore } from "@/store/useCustomersStore";
import { ContentTypeType } from "@/types/contentType.type";
import { StatusFilterNameType } from "@/types/filter.types";
import { MessageStatusType } from "@/types/messageStatus.type";
import { ChatChannel, CustomerCareWindow } from "@/views/modules/messaging-inbox/useChat";
import { Chat, EntryStatus } from "@saysimple-platform/dtos";
import Vue from "vue";

//export function resolveChatStatusFilter(chat: ChatsListChatInterface, ownTeams: any[]): statusFilter[] {
export function resolveChatStatusFilter(chat: ChatsListChatInterface): StatusFilterNameType[] {
    const status: StatusFilterNameType[] = [];

    if (chat.lastConversation?.status?.toLowerCase() === "open") {
        if (!chat.assignedUserId && !chat.assignedTeamId) {
            status.push(StatusFilterNameType.NEW);
        }

        if (chat.assignedUserId && chat.assignedUserId === getUserData().id) {
            status.push(StatusFilterNameType.PERSONAL);
        }

        if (chat.assignedUserId) {
            status.push(StatusFilterNameType.OPEN);
        }

        const { customerSettings } = useCustomersStore();
        if (!customerSettings.expandedTeamFilter && !chat.assignedUserId && chat.assignedTeamId) {
            status.push(StatusFilterNameType.TEAM);
        }

        if (customerSettings.expandedTeamFilter && chat.assignedTeamId) {
            status.push(StatusFilterNameType.TEAM);
        }
    } else {
        status.push(StatusFilterNameType.ARCHIVE);
    }

    return status;
}

export const resolveEntryIcon = (entry: any): string | string[] | undefined => {
    if (!entry) {
        return;
    }

    switch (entry.type.toLowerCase()) {
        case "event":
            return "calendar";
        case "attachment":
        case "asset":
            if (!entry.assets || entry.assets.length === 0) {
                return "paperclip";
            }

            return resolveContentTypeIcon(entry.assets[0].contentType);
        case "location":
            return "map-marker-alt";
        case "information":
            if (entry.variant === "internal-comment") {
                return "comment";
            }

            return "circle-info";
    }
};

// https://www.iana.org/assignments/media-types/media-types.xhtml
// @ts-ignore
export function resolveContentTypeIcon(contentType: ContentTypeType | UploadDto["contentType"]) {
    switch (contentType) {
        case "application/pdf":
            return ["fas", "file-pdf"];
        case "application/msword":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            return ["fas", "file-word"];
        case "application/vnd.ms-excel":
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            return ["fas", "file-excel"];
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            return ["fas", "file-spreadsheet"];
        case "application/zip":
            return ["fas", "file-zipper"];
        case "text/plain":
            return ["fas", "file-lines"];
        case "text/csv":
        case "application/csv":
            return ["fas", "file-csv"];
        case "text/html":
        case "text/css":
        case "text/javascript":
            return ["fas", "file-code"];
    }

    // If we have no hit yet, return by global content type category
    const contentTypeCategory = contentType.split("/")[0].toLowerCase();

    switch (contentTypeCategory) {
        case "audio":
            return ["fa", "waveform"];
        case "video":
            return ["fa", "film"];
        case "image":
            return ["fas", "image"];
    }

    return ["fa", "file"];
}

// @ts-ignore
export function resolveEntryType(entry) {
    if (entry.isAsset || entry.attachments?.length > 0) {
        return "asset";
    }

    if (entry.location) {
        return "location";
    }

    return "text";
}

// @ts-ignore
export const resolveReplyMessage = (entry) => {
    if (!entry) {
        return;
    }

    switch (entry.type) {
        case "text":
            return entry.message;
        case "attachment":
            if (!entry.assets || !entry.assets.length) {
                return;
            }

            return entry.message ?? i18n.t(`entities.conversation.replyTypes.${entry.assets[0].category}`);
        case "location":
            return i18n.t("entities.conversation.replyTypes.location");
    }
};

// @ts-ignore
export const resolveSenderName = (entry, users, contact): string => {
    if (entry?.system) {
        return i18n.t("automated-message");
    }

    return entry?.direction === "out" ? users.find((user: any) => user.id === entry.userId)?.name : contact.name;
};

// @ts-ignore
export const resolveMessageStatusIcon = (status: MessageStatusType | EntryStatus) => {
    switch (status && status.toLowerCase()) {
        case "pending":
            return "clock";
        case "sent":
            return "check";
        case "delivered":
        case "read":
            return "check-double";
        case "declined":
            return "times";
    }
};

// @ts-ignore
export const resolveMessageStatusClass = (status) => {
    switch (status && status.toLowerCase()) {
        case "read":
            return "text-info";
    }
};

// @ts-ignore
export const resolveMessageStatusText = (entry) => {
    if (entry.statusReason) {
        return i18n.t(`status-reasons.${entry.statusReason}`);
    }

    return entry.status;
};

// @ts-ignore
export const resolveRemainingCustomerCareWindow = (chatDatetime, channelWindowLength): CustomerCareWindow => {
    // Is channel length is zero, customer care window is disabled, so handle it like we're within the customer care window.
    if (channelWindowLength === 0) {
        return true;
    }

    // Chat datetime can be undefined when starting a new chat, so when there is no last message yet.
    // In that case, you must send an approved template.
    if (!chatDatetime || !channelWindowLength) {
        return false;
    }

    // example data:
    // now: 27-06-2022 10:01:14
    // chatDate: 26-06-2022 09:29:14
    const now = Vue.prototype.$luxon.DateTime.now();
    const chatDate = Vue.prototype.$luxon.DateTime.fromISO(chatDatetime);

    const differenceMessage = now.diff(chatDate, ["hours", "minutes"]).toObject();

    // Channel custom care window does not work with minutes, so if 24 hours, 0 minutes has been reached, CCW is over.
    if (
        differenceMessage.hours > channelWindowLength ||
        (differenceMessage.hours === channelWindowLength && differenceMessage.minutes > 0)
    ) {
        return false;
    }

    // For ex 24 hours, 0 minutes.
    const channelLength = Vue.prototype.$luxon.Duration.fromObject({
        hours: channelWindowLength,
        minutes: 0,
    });

    const remaining = channelLength
        .minus({ hours: differenceMessage.hours, minutes: differenceMessage.minutes })
        .normalize();

    return {
        hours: remaining.hours,
        minutes: Math.floor(remaining.minutes),
    };
};

export const isChatArchived = (chat: Chat) => chat.status === "closed";
export const isChatAssignedToMeV21 = (assignedUserId?: number | null) => {
    if (!assignedUserId) {
        return false;
    }

    return assignedUserId === getUserData().id;
};

export const shouldAssignV21 = (channel?: ChatChannel) => {
    if (!channel) {
        return false;
    }

    return channel.settings?.find((setting) => setting.name === "mandatoryAssignActive")?.value === "1";
};

// @ts-ignore
export const isChatEnabledV21 = (chat) => {
    let mayChat;

    mayChat = resolveRemainingCustomerCareWindow(chat.lastInboundMessageTime, chat.channel.customerCareWindowLength);

    // Should assign according to channel settings and it IS assigned, then youre allowed to send messages.
    if (shouldAssignV21(chat.channel) && !isChatAssignedToMeV21(chat.lastConversation.assignedUserId)) {
        mayChat = false;
    }

    return mayChat;
};

// @ts-ignore
export const canStartChat = (channel: ChatChannel, contact) => {
    if (["whatsapp", "sms", "test"].includes(channel.platform) && !contact.phone) {
        return false;
    }

    return !(channel.platform === "email" && !contact.email);
};

export function resolveEntryContent(entry: any) {
    const messageType = resolveEntryType(entry);

    const messageContent = {
        direction: entry.direction,
        id: entry.id,
        identityId: entry.identityId,
        message: entry.message,
        components: entry.components,
        status: entry.status,
        time: entry.time,
        system: entry.system,
        type: messageType,
        userId: entry.userId,
        metadata: entry.metadata,
        lastInboundMessageTime: entry.time,
    };

    if (messageType === "location") {
        // @ts-ignore
        messageContent.name = entry.location.name;
        // @ts-ignore
        messageContent.address = entry.location.address;
        // @ts-ignore
        messageContent.url = entry.location.url;
        // @ts-ignore
        messageContent.latitude = entry.location.latitude;
        // @ts-ignore
        messageContent.longitude = entry.location.longitude;
    } else if (messageType === "asset") {
        // @ts-ignore
        messageContent.assets = entry.assets.map((asset: any) => ({
            contentType: asset.contentType,
            filePath: asset.filePath,
            fileName: asset.fileName,
            originalName: asset.originalName,
            category: asset.category,
        }));

        // @ts-ignore
        messageContent.caption = entry.message;
    }

    return messageContent;
}
